/**
 * ViolationCureDaysPanel - Displays cure days countdown and full restart history.
 * Placed inside the Details tab sidebar or as a standalone panel.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Button, TextField, Chip, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import TimerIcon       from "@mui/icons-material/Timer";
import RefreshIcon     from "@mui/icons-material/Refresh";
import HistoryIcon     from "@mui/icons-material/History";
import WarningIcon     from "@mui/icons-material/Warning";
import api from "../../utils/api";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "N/A";

const ViolationCureDaysPanel = ({ violationId, violationDetails }) => {
  const [lastCure,    setLastCure]    = useState(null);
  const [history,     setHistory]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [restartOpen, setRestartOpen] = useState(false);
  const [form,        setForm]        = useState({ days:"", reason:"" });
  const [saving,      setSaving]      = useState(false);
  const [snack,       setSnack]       = useState("");
  const [histOpen,    setHistOpen]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lastRes, timelineRes] = await Promise.allSettled([
        api.get(`/violations/${violationId}/last-cure`),
        api.get(`/violationsTimeline/${violationId}`),
      ]);
      if (lastRes.status === "fulfilled") setLastCure(lastRes.value.data);
      if (timelineRes.status === "fulfilled") {
        const tl = timelineRes.value.data?.timeline || [];
        setHistory(tl.filter(e => e.type === "cure_days" || e.cure_days || e.event_type === "cure_days"));
      }
    } catch {} finally { setLoading(false); }
  }, [violationId]);

  useEffect(() => { load(); }, [load]);

  const handleRestart = async () => {
    if (!form.days || Number(form.days) < 1) return;
    setSaving(true);
    try {
      await api.put(`/violations/${violationId}/update-cure-days`, {
        cure_days: Number(form.days),
        reason: form.reason,
      });
      setSnack("Cure days restarted successfully.");
      setRestartOpen(false);
      setForm({ days:"", reason:"" });
      load();
    } catch { setSnack("Failed to update cure days."); }
    finally { setSaving(false); }
  };

  // Calculate countdown
  const cureDays    = lastCure?.cure_days || violationDetails?.cure_days || 0;
  const startDate   = lastCure?.created_at || violationDetails?.created_at;
  const deadline    = startDate ? new Date(new Date(startDate).getTime() + cureDays * 86400000) : null;
  const today       = new Date();
  const daysLeft    = deadline ? Math.ceil((deadline - today) / 86400000) : null;
  const pct         = (cureDays && daysLeft != null) ? Math.max(0, Math.min(100, Math.round(((cureDays - daysLeft) / cureDays) * 100))) : 0;
  const isOverdue   = daysLeft != null && daysLeft < 0;
  const isWarning   = daysLeft != null && daysLeft >= 0 && daysLeft <= 3;

  const barColor = isOverdue ? "#dc2626" : isWarning ? "#d97706" : "#16a34a";

  return (
    <Paper elevation={0} sx={{ borderRadius:"14px", border:`2px solid ${isOverdue ? "rgba(220,38,38,0.25)" : "rgba(47,99,173,0.12)"}`, p:2.5, mb:2 }}>
      <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:1.5 }}>
        <TimerIcon sx={{ color: isOverdue ? "#dc2626" : "#d97706" }} />
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>Cure Days</Typography>
        {isOverdue && <Chip size="small" label="OVERDUE" sx={{ fontWeight:800, fontSize:10, borderRadius:"6px", bgcolor:"rgba(220,38,38,0.1)", color:"#dc2626" }} />}
        {isWarning && !isOverdue && <Chip size="small" label="DUE SOON" sx={{ fontWeight:800, fontSize:10, borderRadius:"6px", bgcolor:"rgba(217,119,6,0.1)", color:"#d97706" }} />}
      </Box>

      {loading ? <CircularProgress size={20} /> : (
        <>
          {snack && <Alert severity={snack.includes("Failed") ? "error" : "success"} onClose={() => setSnack("")} sx={{ mb:1.5, borderRadius:2 }}>{snack}</Alert>}

          <Box sx={{ display:"flex", justifyContent:"space-between", mb:1 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight:800, color: isOverdue ? "#dc2626" : "#223142", lineHeight:1 }}>
                {daysLeft != null ? (isOverdue ? Math.abs(daysLeft) : daysLeft) : cureDays}
              </Typography>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>
                {daysLeft != null ? (isOverdue ? "days overdue" : "days remaining") : "total cure days"}
              </Typography>
            </Box>
            <Box sx={{ textAlign:"right" }}>
              <Typography variant="body2" sx={{ fontWeight:600, color:"#223142" }}>{cureDays} days total</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>Deadline: {fmtDate(deadline)}</Typography>
            </Box>
          </Box>

          <LinearProgress variant="determinate" value={pct}
            sx={{ height:8, borderRadius:4, bgcolor:"rgba(0,0,0,0.06)", mb:1.5,
              "& .MuiLinearProgress-bar":{ bgcolor: barColor } }} />

          <Box sx={{ display:"flex", gap:1 }}>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => setRestartOpen(true)}
              sx={{ borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:11 }}>
              Restart
            </Button>
            {history.length > 0 && (
              <Button size="small" variant="text" startIcon={<HistoryIcon fontSize="small" />}
                onClick={() => setHistOpen(true)}
                sx={{ borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:11, color:"#6b778c" }}>
                History ({history.length})
              </Button>
            )}
          </Box>
        </>
      )}

      {/* Restart Dialog */}
      <Dialog open={restartOpen} onClose={() => setRestartOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#223142" }}>Restart Cure Days</DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb:2, borderRadius:2 }}>
            This will reset the cure period timer and create an audit record.
          </Alert>
          <TextField label="New Cure Days *" type="number" fullWidth size="small" value={form.days}
            onChange={e => setForm(f => ({ ...f, days: e.target.value }))}
            inputProps={{ min:1, max:365 }}
            sx={{ mb:2, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
          <TextField label="Reason (optional)" fullWidth multiline rows={2} size="small" value={form.reason}
            onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, gap:1 }}>
          <Button onClick={() => setRestartOpen(false)} variant="outlined" sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          <Button onClick={handleRestart} variant="contained" disabled={saving || !form.days}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, boxShadow:"none", bgcolor:"#d97706", "&:hover":{ bgcolor:"#b45309", boxShadow:"none" } }}>
            {saving ? "Saving…" : "Restart"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={histOpen} onClose={() => setHistOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#223142" }}>Cure Days History</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
              <TableRow>{["Date","Days","Reason","By"].map(h => <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {history.map((h, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontSize:12, color:"#6b778c" }}>{fmtDate(h.created_at || h.timestamp)}</TableCell>
                  <TableCell sx={{ fontWeight:600, color:"#223142" }}>{h.cure_days || h.days || "-"}</TableCell>
                  <TableCell sx={{ fontSize:12, color:"#6b778c" }}>{h.reason || h.notes || "-"}</TableCell>
                  <TableCell sx={{ fontSize:12, color:"#6b778c" }}>{h.changed_by || h.user_name || "System"}</TableCell>
                </TableRow>
              ))}
              {!history.length && <TableRow><TableCell colSpan={4} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No history.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2 }}>
          <Button onClick={() => setHistOpen(false)} variant="outlined" sx={{ borderRadius:"10px", textTransform:"none" }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ViolationCureDaysPanel;
